import { escapeHtml } from '@/lib/html-escape'
import { formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

/** A6 landscape — single constant so it is trivial to adjust. */
const PAGE_SIZE = '148mm 105mm'

/** Tên người nhận dài hơn mức này in cỡ chữ nhỏ hơn. */
const LONG_NAME = 60

export interface BoxLabelData {
    customerName: string
    customerPhone?: string
    shippingAddress?: string
    shippingWard?: string
    shippingProvince?: string
    /** Đơn vị vận chuyển (GHTK, Tự giao...). Render dưới địa chỉ, cùng cỡ chữ. */
    shippingCarrierName?: string
    totalAmount: number
    paidAmount: number
    // true = thu (in số tiền), false = "Không thu tiền", null/undefined = "không
    // chọn" → ẩn hẳn dòng COD.
    collectCod: boolean | null | undefined
    /** Ghi đè số tiền COD cho riêng lần in này. Bỏ trống = tính
     *  `totalAmount - paidAmount` như cũ. 0 là giá trị hợp lệ, không phải
     *  "chưa nhập" — nên dùng `??` chứ không phải `||` khi đọc. */
    codAmount?: number
    shippingFeePayer?: 'customer' | 'company'
    /** Free-text written by staff; includes box count, delivery instructions, etc. */
    deliveryNote?: string
    /** Rendered small at the bottom as the sender block. */
    companyName?: string
    /** Rendered small ở footer cạnh companyName, cho khách gọi HTX khi có vấn đề giao. */
    companyPhone?: string
    /** Data URL của QR trỏ về màn chi tiết đơn (giống phiếu giao hàng). Center dưới ghi chú. */
    qrDataUrl?: string
    /** Số kiện: in mỗi kiện một tờ, đánh "Kiện i/N". ≤ 1 hoặc bỏ trống = một tờ, không đánh số. */
    packageCount?: number
    /** Đơn quà web (§14.21): tem đi theo hộp tới người nhận — không in dòng
     *  COD khi không thu gì, không in "Nhận trả cước" khi ship đã trả trước. */
    gift?: { shippingPrepaid: boolean }
}

/**
 * Builds a self-contained HTML string for an A6-landscape (148×105mm) box /
 * shipping label. Printed via printReceiptHtml(). Large bold typography so
 * the label is readable from a distance or when a photo is taken.
 *
 * Every customer-controlled field is passed through escapeHtml to prevent
 * stored-XSS via the same-origin iframe used by printReceiptHtml.
 */
export function buildBoxLabelHtml(data: BoxLabelData): string {
    // Full address line — join non-empty parts
    const addressParts = [
        data.shippingAddress,
        data.shippingWard,
        data.shippingProvince,
    ].filter(Boolean)
    const addressHtml =
        addressParts.length > 0
            ? `<div class="addr">${addressParts.map((p) => escapeHtml(p ?? '')).join(', ')}</div>`
            : ''

    // COD line — thu → số tiền; không thu → "Không thu tiền"; không chọn
    // (null/undefined) → ẩn hẳn.
    const codValue = data.codAmount ?? data.totalAmount - data.paidAmount
    // Đơn quà: COD vẫn hiện số tài xế thu nếu có; không thu gì thì bỏ hẳn dòng.
    const giftNoCod =
        data.gift !== undefined && (data.collectCod !== true || codValue <= 0)
    const codLine = giftNoCod
        ? ''
        : data.collectCod === true
          ? `<div class="flag cod"><span class="flag-lbl">${m.box_label_cod_collect()}:</span> <strong>${escapeHtml(formatVnd(codValue))}</strong></div>`
          : data.collectCod === false
            ? `<div class="flag"><span class="flag-lbl">${m.box_label_cod_none()}</span></div>`
            : ''

    // Freight line — no amount; wording follows who bears the freight. Recipient
    // pays ("Nhận trả cước") when payer === 'customer', co-op pays ("Gửi trả
    // cước") when 'company'. Omitted entirely when unset (no selection). Depot
    // settles the amount later.
    const freightLine = data.gift?.shippingPrepaid
        ? ''
        : data.shippingFeePayer === 'customer'
          ? `<div class="flag"><span class="flag-lbl">${m.order_freight_status_customer()}</span></div>`
          : data.shippingFeePayer === 'company'
            ? `<div class="flag"><span class="flag-lbl">${m.order_freight_status_company()}</span></div>`
            : ''

    // The co-op always lets the recipient inspect its own parcels, even when
    // the Viettel Post booking was made without inspection to save the fee.
    const inspectionLine = `<div class="flag"><span class="flag-lbl">${m.box_label_inspection_allowed()}</span></div>`

    // Delivery note — omit when empty
    const noteLine = data.deliveryNote
        ? `<div class="note"><span class="note-lbl">${m.box_label_note()}:</span> ${escapeHtml(data.deliveryNote)}</div>`
        : ''

    // Shipping carrier — omit when empty; prefix "Vận chuyển:" cho rõ nghĩa với nhân viên đóng gói.
    const carrierLine = data.shippingCarrierName
        ? `<div class="carrier"><span class="carrier-lbl">${m.box_label_carrier()}:</span> ${escapeHtml(data.shippingCarrierName)}</div>`
        : ''

    // QR block — trung tâm dưới ghi chú. Text label giống phiếu giao hàng để nhất quán.
    const qrBlock = data.qrDataUrl
        ? `<div class="qr-wrap"><img class="qr" src="${data.qrDataUrl}" alt="QR" /><div class="qr-lbl">${m.delivery_slip_qr_label()}</div></div>`
        : ''

    // Sender block — 1 dòng "Company — SĐT: xxx". Chỉ hiện khi có ít nhất companyName.
    const senderBits: string[] = []
    if (data.companyName) senderBits.push(escapeHtml(data.companyName))
    if (data.companyPhone)
        senderBits.push(
            `${m.box_label_sender_phone()}: ${escapeHtml(data.companyPhone)}`,
        )
    const senderBlock =
        senderBits.length > 0
            ? `<div class="sender">${senderBits.join(' — ')}</div>`
            : ''

    // Một tờ cho mỗi kiện: nhân viên dán mỗi thùng một nhãn, hãng kiểm đếm theo
    // số "Kiện i/N". Đơn một kiện giữ nguyên nhãn cũ, không đánh số.
    const total = Math.max(1, Math.floor(data.packageCount ?? 1))
    const pages = Array.from({ length: total }, (_, i) => {
        const indexLine =
            total > 1
                ? `<div class="package-index">${m.box_label_package_index({ index: String(i + 1), total: String(total) })}</div>`
                : ''
        return `<div class="page">
<div class="content">
${indexLine}
<div class="recipient-name${data.customerName.length > LONG_NAME ? ' long' : ''}">${escapeHtml(data.customerName)}</div>
${data.customerPhone ? `<div class="phone">${escapeHtml(data.customerPhone)}</div>` : ''}
${addressHtml}
${carrierLine}
<div class="sep"></div>
<div class="lower-row">
<div class="lower-left">
${codLine}
${freightLine}
${inspectionLine}
${noteLine}
</div>
<div class="lower-right">
${qrBlock}
</div>
</div>
${senderBlock}
</div>
</div>`
    }).join('\n')

    return `<!DOCTYPE html>
<html lang="vi"><head><meta charset="utf-8" />
<title>${m.box_label_recipient()}</title>
<style>
@page { size: ${PAGE_SIZE}; margin: 4mm 5mm; }
* { margin: 0; padding: 0; box-sizing: border-box; }
html, body { width: 148mm; }
body { font-family: 'Roboto', 'Segoe UI', system-ui, sans-serif; font-size: 12px; color: #000; line-height: 1.3; display: flex; flex-direction: column; }
/* Fixed page height (105mm minus the 4mm top/bottom margins) + flex column so
   the content block can center itself vertically on the A6 label. One .page
   per parcel; each breaks onto its own sheet. */
.page { height: 97mm; display: flex; flex-direction: column; break-after: page; page-break-after: always; }
.page:last-child { break-after: auto; page-break-after: auto; }
/* Auto vertical margins = centered when the content is shorter than the page,
   and gracefully top-aligned (no clipping at both ends) when it overflows. */
.content { margin: auto 0; min-width: 0; overflow-wrap: anywhere; }
.package-index { font-size: 20px; font-weight: 700; text-align: right; margin-bottom: 2px; }
.recipient-name { font-size: 26px; font-weight: 700; line-height: 1.2; margin-bottom: 2px; }
/* Recipient names run up to 120 characters (rule N5): long ones print smaller
   so the address and COD still fit; .content's overflow-wrap breaks unbroken text. */
.recipient-name.long { font-size: 20px; }
.phone { font-size: 22px; font-weight: 700; margin-bottom: 3px; }
.addr { font-size: 22px; font-weight: 700; margin-bottom: 3px; line-height: 1.3; }
.carrier { font-size: 22px; font-weight: 700; margin-bottom: 4px; line-height: 1.3; }
.carrier-lbl { font-weight: 600; }
.sep { border-top: 1.5px dashed #000; margin: 4px 0; }
/* Lower block prints at the same 22px as the recipient block — the label is
   read at arm's length at the packing bench, so both halves must match. */
.flag { display: flex; align-items: baseline; gap: 8px; font-size: 22px; font-weight: 700; margin: 2px 0; }
.flag.cod { font-size: 24px; }
.flag-lbl { font-weight: 600; }
.note { font-size: 22px; font-weight: 700; margin-top: 3px; font-style: italic; }
.note-lbl { font-weight: 600; font-style: normal; }
/* Lower half is a two-column row: money/freight/note on the left, an enlarged
   QR pinned to the right (the area staff asked for). align-items:flex-start so
   the QR top lines up with the first COD line. */
.lower-row { display: flex; align-items: flex-start; gap: 6mm; }
.lower-left { flex: 1; min-width: 0; }
.lower-right { flex: 0 0 auto; }
/* QR bumped to 32mm (from 20mm) — big and unmistakable in the top-right block. */
.qr-wrap { text-align: center; }
.qr { width: 32mm; height: 32mm; display: inline-block; }
.qr-lbl { font-size: 11px; margin-top: 1px; }
.sender { font-size: 10px; color: #555; text-transform: uppercase; margin-top: 2px; border-top: 0.5px solid #aaa; padding-top: 2px; text-align: center; }
</style></head>
<body>
${pages}
</body></html>`
}
